import { ArrowLeft, Check, Crown, Sparkles, X, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/app/components/ui/button";
import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { PLAN_TIERS } from "./constants";
import { getCompanyDashboardPath } from "./paths";
import "./styles.css";

const ICONS = {
	starter: Zap,
	corporate: Sparkles,
	enterprise: Crown,
};

export default function UpgradePage() {
	const { plan } = useCompanyDashboard();

	return (
		<div className="company-dashboard-app min-h-screen bg-slate-50">
			<div className="sticky top-0 z-10 border-b border-border bg-white/95 backdrop-blur-sm">
				<div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
					<Button variant="ghost" asChild>
						<Link to={getCompanyDashboardPath("home")}>
							<ArrowLeft className="mr-2 h-4 w-4" />
							Back to Dashboard
						</Link>
					</Button>
					<p className="text-sm text-muted-foreground">
						Current plan: <span className="font-medium text-foreground capitalize">{plan}</span>
					</p>
				</div>
			</div>

			<div className="mx-auto max-w-6xl px-4 py-10">
				<div className="mx-auto mb-10 max-w-2xl text-center">
					<h1 className="text-3xl font-bold text-foreground">Upgrade Your Employer Plan</h1>
					<p className="mt-3 text-base text-muted-foreground">
						These plans mirror the Careerfy structure inside the professional app while the billing backend catches up.
					</p>
				</div>

				<div className="grid gap-5 lg:grid-cols-3">
					{PLAN_TIERS.map((tier) => {
						const isCurrentPlan = tier.id === plan;
						const Icon = ICONS[tier.id];
						return (
							<div
								key={tier.id}
								className={`relative rounded-3xl p-6 ${
									tier.enterprise
										? "company-dashboard-gradient text-white shadow-xl"
										: tier.popular
											? "border-2 border-primary-400 bg-white shadow-lg"
											: "border border-border bg-white"
								}`}
							>
								{tier.popular && !isCurrentPlan ? (
									<div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary-700 px-3 py-1 text-xs font-medium text-white">
										Recommended
									</div>
								) : null}

								{isCurrentPlan ? (
									<div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-foreground">
										Current Plan
									</div>
								) : null}

								<div
									className={`mb-5 inline-flex h-11 w-11 items-center justify-center rounded-2xl ${tier.enterprise ? "bg-white/10" : "bg-blue-50"}`}
								>
									<Icon className={`h-5 w-5 ${tier.enterprise ? "text-amber-300" : "text-primary-700"}`} />
								</div>

								<h2 className={`text-2xl font-bold ${tier.enterprise ? "text-white" : "text-foreground"}`}>
									{tier.name}
								</h2>
								<p className={`mt-1 text-sm font-medium ${tier.enterprise ? "text-white/80" : "text-primary-700"}`}>
									{tier.tagline}
								</p>
								<div className="mt-5">
									<span className={`text-4xl font-bold ${tier.enterprise ? "text-white" : "text-foreground"}`}>
										{tier.price}
									</span>
									<span className={`ml-2 text-sm ${tier.enterprise ? "text-white/70" : "text-muted-foreground"}`}>
										{tier.period}
									</span>
								</div>
								<p className={`mt-3 text-sm ${tier.enterprise ? "text-white/80" : "text-muted-foreground"}`}>
									{tier.description}
								</p>

								<ul className="mt-6 space-y-3">
									{tier.features.map((feature) => (
										<li key={feature.text} className="flex items-start gap-3">
											<div
												className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${feature.included ? (tier.enterprise ? "bg-white/10" : "bg-green-50") : tier.enterprise ? "bg-white/10" : "bg-secondary"}`}
											>
												{feature.included ? (
													<Check className={`h-3.5 w-3.5 ${tier.enterprise ? "text-white" : "text-green-700"}`} />
												) : (
													<X className={`h-3.5 w-3.5 ${tier.enterprise ? "text-white/60" : "text-muted-foreground"}`} />
												)}
											</div>
											<span
												className={`text-sm ${tier.enterprise ? (feature.included ? "text-white" : "text-white/55") : feature.included ? "text-foreground" : "text-muted-foreground"}`}
											>
												{feature.text}
											</span>
										</li>
									))}
								</ul>

								<Button
									className={`mt-8 w-full ${tier.enterprise ? "bg-white text-primary-700 hover:bg-white/90" : ""}`}
									variant={tier.enterprise ? "default" : isCurrentPlan ? "outline" : "default"}
									onClick={() => {
										if (isCurrentPlan) return;
										toast.info("Upgrade actions are not connected to a live employer billing backend yet.");
									}}
								>
									{isCurrentPlan ? "Current Plan" : `Choose ${tier.name}`}
								</Button>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
